import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Card,
  message,
  InputNumber,
  Checkbox,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import MaturityScheduleService from '../../service/maturity-schedule-service/MaturitySchedule.service';
import CreditCardService from '../../service/credit-card-service/Credit-card.service';
import EmployeeService from '../../service/employee-service/Employee.service';
import { MaturityScheduleRequest } from '../../model/MaturitySchedule.model';
import { CreditCardResponse2 } from '../../model/Credit-card.model';
import EmployeeResponse from '../../model/Employee.model';
import useEmployee from '../../hook/useEmployee';
import { useIsAdmin } from '../../hook/useRole';

interface RenewalPeriod {
  dueDate: string;
  repaymentAmount: number;
  feeApplied: number;
  notes: string;
  creditCardId: number;
  employeeIds: number[];
  useDefaultFee: boolean;
}

interface RenewalPeriodsModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const RenewalPeriodsModal: React.FC<RenewalPeriodsModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [creditCards, setCreditCards] = useState<CreditCardResponse2[]>([]);
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const { employee } = useEmployee();
  const [renewalPeriods, setRenewalPeriods] = useState<RenewalPeriod[]>([
    {
      dueDate: '',
      repaymentAmount: 0,
      feeApplied: 0,
      notes: '',
      creditCardId: 0,
      employeeIds: [],
      useDefaultFee: false,
    },
  ]);
  const isAdmin = useIsAdmin();

  // Load credit cards and employees when modal opens
  useEffect(() => {
    if (visible) {
      loadCreditCards();
      loadEmployees();
    }
  }, [visible]);

  const loadCreditCards = async () => {
    try {
      setLoadingCards(true);
      const cards = await CreditCardService.getCreditCardList();
      // Filter out cards with KHONG_LAM status (disabled cards)
      const activeCards = cards.filter(
        (card) => card.newStatus !== 'KHONG_LAM'
      );
      setCreditCards(activeCards);
    } catch (error) {
      message.error('Không thể tải danh sách thẻ tín dụng');
      console.error('Error loading credit cards:', error);
    } finally {
      setLoadingCards(false);
    }
  };

  const loadEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const employeeList = await EmployeeService.getEmployeeList();
      setEmployees(employeeList);
    } catch (error) {
      message.error('Không thể tải danh sách nhân viên');
      console.error('Error loading employees:', error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const formatCardNumber = (cardNumber: string) => {
    if (!cardNumber) return '';
    const lastFour = cardNumber.slice(-4);
    return `***${lastFour}`;
  };

  const addRenewalPeriod = () => {
    setRenewalPeriods([
      ...renewalPeriods,
      {
        dueDate: '',
        repaymentAmount: 0,
        feeApplied: 0,
        notes: '',
        creditCardId: 0,
        employeeIds: [],
        useDefaultFee: false,
      },
    ]);
  };

  const removeRenewalPeriod = (index: number) => {
    if (renewalPeriods.length > 1) {
      const newPeriods = renewalPeriods.filter((_, i) => i !== index);
      setRenewalPeriods(newPeriods);
    }
  };

  // Get default fee rate for selected card
  const getDefaultFeeRate = (cardId: number) => {
    const card = creditCards.find((c) => c.id === cardId);
    return card?.defaultFeeRate || null;
  };

  // Handle checkbox change for using default fee
  const handleUseDefaultFeeChange = (index: number, checked: boolean) => {
    const newPeriods = [...renewalPeriods];
    newPeriods[index] = {
      ...newPeriods[index],
      useDefaultFee: checked,
      feeApplied: checked
        ? getDefaultFeeRate(newPeriods[index].creditCardId) || 0
        : 0,
    };
    setRenewalPeriods(newPeriods);
  };

  const updateRenewalPeriod = (
    index: number,
    field: keyof RenewalPeriod,
    value: any
  ) => {
    const newPeriods = [...renewalPeriods];
    newPeriods[index] = { ...newPeriods[index], [field]: value };

    // If card is changed, reset useDefaultFee and feeApplied
    if (field === 'creditCardId') {
      newPeriods[index].useDefaultFee = false;
      newPeriods[index].feeApplied = 0;
    }

    setRenewalPeriods(newPeriods);
  };

  const handleSubmit = async () => {
    try {
      if (!employee?.id) {
        message.error('Không tìm thấy thông tin nhân viên');
        return;
      }

      // Nếu không phải admin, set employeeIds cho từng period trước khi validate
      let periodsToValidate = renewalPeriods;
      if (!isAdmin) {
        periodsToValidate = renewalPeriods.map((period) => ({
          ...period,
          employeeIds: [employee.id],
        }));
        setRenewalPeriods(periodsToValidate); // đồng bộ lại state
      }

      // Validate all periods
      const isValid = periodsToValidate.every(
        (period) =>
          period.dueDate &&
          period.repaymentAmount > 0 &&
          period.creditCardId > 0 &&
          period.employeeIds.length > 0
      );

      if (!isValid) {
        message.error('Vui lòng điền đầy đủ thông tin cho tất cả kỳ đáo hạn');
        return;
      }

      setLoading(true);

      // Convert to MaturityScheduleRequest format
      const requests: MaturityScheduleRequest[] = periodsToValidate.map(
        (period) => ({
          dueDate: period.dueDate,
          repaymentAmount: period.repaymentAmount,
          feeApplied: period.feeApplied,
          notes: period.notes,
          creditCardId: period.creditCardId,
          employeeIds: isAdmin ? period.employeeIds : [employee.id],
        })
      );

      await MaturityScheduleService.createMultiSchedule(requests, employee.id);

      message.success('Tạo kỳ đáo hạn thành công!');
      onSuccess();
      handleClose();
    } catch (error) {
      message.error('Có lỗi xảy ra khi tạo kỳ đáo hạn');
      console.error('Error creating renewal periods:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setRenewalPeriods([
      {
        dueDate: '',
        repaymentAmount: 0,
        feeApplied: 0,
        notes: '',
        creditCardId: 0,
        employeeIds: [],
        useDefaultFee: false,
      },
    ]);
    onClose();
  };

  return (
    <Modal
      title={<span className="text-sm">Tạo kỳ đáo hạn mới</span>}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={900}
      className="renewal-periods-modal"
    >
      <div className="space-y-2">
        {renewalPeriods.map((period, index) => (
          <Card
            key={index}
            size="small"
            title={<span className="text-xs">{`Kỳ ${index + 1}`}</span>}
            extra={
              renewalPeriods.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeRenewalPeriod(index)}
                  size="small"
                />
              )
            }
            className="border border-gray-200"
            bodyStyle={{ padding: '12px' }}
          >
            <div className="space-y-2">
              {/* Credit Card Select - Full Width at Top */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">
                  Thẻ TD <span className="text-red-500">*</span>
                </label>
                <Select
                  placeholder="Chọn thẻ"
                  value={period.creditCardId || undefined}
                  onChange={(value) =>
                    updateRenewalPeriod(index, 'creditCardId', value)
                  }
                  className="w-full"
                  size="small"
                  showSearch
                  loading={loadingCards}
                  filterOption={(input, option) => {
                    if (!option) return false;
                    // Find the card data from creditCards array
                    const card = creditCards.find((c) => c.id === option.value);
                    if (!card) return false;

                    const searchInput = input.toLowerCase();
                    const lastFour = card.cardNumber?.slice(-4) || '';
                    const cardHolderName =
                      card.cardHolderName?.toLowerCase() || '';
                    const bankName = card.bank?.name?.toLowerCase() || '';
                    const cardTypeName =
                      card.cardType?.name?.toLowerCase() || '';

                    // Search by last 4 digits, cardholder name, bank name, or card type
                    return (
                      lastFour.includes(searchInput) ||
                      cardHolderName.includes(searchInput) ||
                      bankName.includes(searchInput) ||
                      cardTypeName.includes(searchInput)
                    );
                  }}
                  optionRender={(option) => {
                    // Find the card data from creditCards array
                    const card = creditCards.find((c) => c.id === option.value);
                    if (!card) return null;

                    return (
                      <div className="py-2">
                        <div className="font-medium text-gray-800">
                          {formatCardNumber(card.cardNumber)} -{' '}
                          {card.cardType?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Ngân hàng:</span>{' '}
                          {card.bank?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Chủ thẻ:</span>{' '}
                          {card.cardHolderName || 'N/A'}
                        </div>
                      </div>
                    );
                  }}
                  options={creditCards.map((card) => ({
                    value: card.id,
                    label: `${formatCardNumber(card.cardNumber)} - ${card.cardType?.name || 'N/A'} - ${card.bank?.name || 'N/A'} - ${card.cardHolderName || 'N/A'}`,
                    key: card.id,
                  }))}
                />
              </div>

              {/* Grid Layout for Other Fields */}
              <div className="grid grid-cols-2 gap-2">
                {/* Due Date */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Ngày đáo hạn <span className="text-red-500">*</span>
                  </label>
                  <DatePicker
                    placeholder="Chọn ngày"
                    value={period.dueDate ? dayjs(period.dueDate) : null}
                    onChange={(date) =>
                      updateRenewalPeriod(
                        index,
                        'dueDate',
                        date ? date.format('YYYY-MM-DD') : ''
                      )
                    }
                    className="w-full"
                    size="small"
                    format="DD/MM/YYYY"
                  />
                </div>

                {/* Employee Multi Select */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Nhân viên <span className="text-red-500">*</span>
                  </label>
                  {isAdmin ? (
                    <Select
                      mode="multiple"
                      placeholder="Chọn NV"
                      value={period.employeeIds}
                      onChange={(value) =>
                        updateRenewalPeriod(index, 'employeeIds', value)
                      }
                      className="w-full"
                      size="small"
                      showSearch
                      loading={loadingEmployees}
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={employees.map((emp) => ({
                        value: emp.id,
                        label: emp.name,
                        key: emp.id,
                      }))}
                      maxTagCount="responsive"
                    />
                  ) : (
                    <div className="text-sm text-gray-500 border rounded px-2 py-1 bg-gray-50">
                      {employee?.name}
                    </div>
                  )}
                </div>

                {/* Repayment Amount */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Số tiền <span className="text-red-500">*</span>
                  </label>
                  <InputNumber
                    placeholder="Nhập số tiền"
                    value={period.repaymentAmount || undefined}
                    onChange={(value) =>
                      updateRenewalPeriod(index, 'repaymentAmount', value || 0)
                    }
                    className="w-full"
                    size="small"
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={(value) =>
                      Number(value!.replace(/\$\s?|(,*)/g, ''))
                    }
                    min={0}
                  />
                </div>

                {/* Fee Applied */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Phí (%)
                    {period.creditCardId > 0 && (
                      <span className="text-gray-500 text-xs ml-1">
                        (MD:{' '}
                        {getDefaultFeeRate(period.creditCardId) !== null
                          ? `${getDefaultFeeRate(period.creditCardId)}%`
                          : 'x'}
                        )
                      </span>
                    )}
                  </label>
                  <div className="space-y-1">
                    <InputNumber
                      placeholder="Nhập phí"
                      value={period.feeApplied || undefined}
                      onChange={(value) =>
                        updateRenewalPeriod(index, 'feeApplied', value || 0)
                      }
                      className="w-full"
                      size="small"
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={(value) =>
                        Number(value!.replace(/\$\s?|(,*)/g, ''))
                      }
                      min={0}
                      disabled={period.useDefaultFee}
                    />
                    {period.creditCardId > 0 &&
                      getDefaultFeeRate(period.creditCardId) !== null && (
                        <Checkbox
                          checked={period.useDefaultFee}
                          onChange={(e) =>
                            handleUseDefaultFeeChange(index, e.target.checked)
                          }
                          className="text-xs"
                        >
                          Dùng phí MD
                        </Checkbox>
                      )}
                  </div>
                </div>
              </div>

              {/* Notes - Full Width at Bottom */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">
                  Ghi chú
                </label>
                <Input.TextArea
                  placeholder="Nhập ghi chú"
                  value={period.notes}
                  onChange={(e) =>
                    updateRenewalPeriod(index, 'notes', e.target.value)
                  }
                  className="w-full"
                  size="small"
                  rows={2}
                />
              </div>
            </div>
          </Card>
        ))}

        {/* Add Renewal Period Button */}
        <div className="text-center">
          <Button
            type="dashed"
            onClick={addRenewalPeriod}
            icon={<PlusOutlined />}
            size="small"
            className="w-full border border-dashed border-gray-300 hover:border-blue-400 h-8"
          >
            Thêm kỳ đáo hạn
          </Button>
        </div>

        {/* Submit Button */}
        <div className="pt-2">
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            className="w-full bg-blue-600 hover:bg-blue-700"
            size="small"
          >
            Tạo tất cả kỳ đáo hạn
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RenewalPeriodsModal;
